import { ButtonType, DialogFooterButtonsWidget, DialogType, TabStyles } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';

import { CommonStyles } from '~common/react/CommonStyles';
import { ModalInstance, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Messages } from '~src/codegen/Localize';
import { PublishedConnectionExtractRefreshLevelOptions } from '~src/publishedConnection/components/scheduledTasks/PublishedConnectionExtractRefreshLevelOptions';
import { PublishedConnectionScheduledTaskTablesGrid } from '~src/publishedConnection/components/scheduledTasks/PublishedConnectionScheduledTaskTablesGrid';
import { PublishedConnectionTaskRefreshTypeDropdown } from '~src/publishedConnection/components/scheduledTasks/PublishedConnectionTaskRefreshTypeDropdown';
import { PublishedConnectionExtractRefreshLevel } from '~src/publishedConnection/types/PublishedConnectionExtractRefreshLevel';
import { PublishedConnectionTaskTableInfo } from '~src/publishedConnection/types/PublishedConnectionTaskTableInfo';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { cssClass } from '~src/style';
import { L10n } from '~src/ts/L10n/L10n';
import { ExtractTaskType } from '~src/ts/ServerApi/types/IExtractTask';

const cssPrefix = 'PublishedConnectionCreateTaskDialogContent';

const rootClass = cssClass(cssPrefix, 'root', {
  display: 'flex',
  flexDirection: 'column'
});

const topPaneClass = cssClass(cssPrefix, 'topPane', {
  flex: 'none'
});

const dialogMessageClass = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const paddingBottomClass = cssClass(cssPrefix, 'paddingBottom', {
  paddingBottom: CommonStyles.Sizes.LargeSpace,
});

const refreshTypeButtonClass = cssClass(cssPrefix, 'refreshTypeButton', {
  width: '250px'
});

const bottomPaneClass = cssClass(cssPrefix, 'bottomPane', {
  flex: 1,
  flexDirection: 'column'
});

const footerClass = cssClass(cssPrefix, 'footerClass', {
  flex: 'none',
});

const tablesContainerClass = cssClass(cssPrefix, 'tablesContainer', {
  borderLeft: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderRight: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderBottom: `1px solid ${CommonStyles.Colors.Grey203}`,
  marginBottom: TabStyles.Spacing.LargeSpace,
  flex: 1,
  overflow: 'auto',
  height: 250
});

const sortTablesByName = (a: PublishedConnectionTaskTableInfo, b: PublishedConnectionTaskTableInfo) => (a.alias > b.alias) ? 1 : -1;

export type PublishedConnectionTableExtractsRefreshNowDialogContentProps = Readonly<{
  close: () => void;
  dismiss: () => void;
  publishedConnectionId: string;
  tables: ReadonlyArray<PublishedConnectionTaskTableInfo>;
}>;

export function PublishedConnectionTableExtractsRefreshNowDialogContent(props: PublishedConnectionTableExtractsRefreshNowDialogContentProps): JSX.Element {
  const { close, dismiss, publishedConnectionId, tables } = props;

  const sortedTables = React.useMemo(() => [...tables].sort(sortTablesByName), [tables]);
  const isGoButtonDisabled = React.useMemo(() => tables.length === 0, [tables]);
  const [extractRefreshLevel, setExtractRefreshLevel] = React.useState<PublishedConnectionExtractRefreshLevel>(
    sortedTables.length > 1
      ? PublishedConnectionExtractRefreshLevel.Database
      : PublishedConnectionExtractRefreshLevel.Table
  );
  const handleRadioSelect = React.useCallback((value) => setExtractRefreshLevel(value), [setExtractRefreshLevel]);

  const handleCreateTask = React.useCallback(
    () => {
      const tableLuids = sortedTables.map(table => table.luid);
      PublishedConnectionTables.runExtractRefreshNow(
        publishedConnectionId,
        tableLuids,
        ExtractTaskType.ExtractServiceFullRefresh,
        extractRefreshLevel
      ).then(result => {
        PublishedConnectionTables.toast(
          result,
          Messages.extractTasksRefreshNowOnPublishedConnectionTableCreateSuccess(),
          Messages.extractTasksRefreshNowOnPublishedConnectionTableCreateError()
        );
        if (!(result.errors && result.errors.length > 0)) {
          close();
        }
      }).catch(error => {
        PublishedConnectionTables.popError(Messages.extractTasksRefreshNowOnPublishedConnectionTableCreateError());
      });
    },
    [close, extractRefreshLevel, publishedConnectionId, sortedTables]
  );

  return (
    <div className={rootClass}>
      <div className={topPaneClass}>
        <div className={dialogMessageClass}>
          {Messages.extractTasksRefreshNowOnPublishedConnectionTablePrompt({
            VIRTUAL_TABLE_COUNT: sortedTables.length,
            VIRTUAL_TABLE_COUNT_LOC: L10n.localizedCount(sortedTables.length),
            VIRTUAL_TABLE_NAME: sortedTables.length > 0 ? L10n.elideIfLong(sortedTables[0].alias) : ''
          })}
        </div>

        {sortedTables.length > 1
          ? <PublishedConnectionExtractRefreshLevelOptions selected={extractRefreshLevel} onChange={handleRadioSelect}/>
          : null
        }
        <div className={classes(refreshTypeButtonClass, paddingBottomClass)}>
          <PublishedConnectionTaskRefreshTypeDropdown/>
        </div>

      </div>
      <div className={bottomPaneClass}>
        <div className={tablesContainerClass}>
          <PublishedConnectionScheduledTaskTablesGrid tables={sortedTables}/>
        </div>

        <div className={footerClass}>
          <DialogFooterButtonsWidget
            buttons={[{
              handleClick: dismiss,
              buttonType: ButtonType.Outline,
              children: Messages.cancel(),
              testId: 'published-connection-table-refresh-now-cancel'
            }, {
              handleClick: handleCreateTask,
              buttonType: ButtonType.Primary,
              children: Messages.publishedConnectionTableExtractsRefreshNow(),
              testId: 'published-connection-table-refresh-now-go',
              disabled: isGoButtonDisabled
            }]}
            testId={'published-connection-table-refresh-now-footer'}
          />
        </div>
      </div>
    </div>
  );
}

export function openPublishedConnectionTableRefreshNowDialogDialog(
  publishedConnectionId: string,
  tables: ReadonlyArray<PublishedConnectionTaskTableInfo>
): Promise<any> {
  let modalInstance: ModalInstance;
  const close = () => modalInstance.close();
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open({
    type: DialogType.NormalWithTitleBar,
    title: Messages.publishedConnectionTableExtractsRefreshNow(),
    dialogContent: (
      <PublishedConnectionTableExtractsRefreshNowDialogContent
        close={close}
        dismiss={dismiss}
        publishedConnectionId={publishedConnectionId}
        tables={tables}
      />
    ),
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width700px,
    shouldRecenterOnUpdate: true
  });
  return modalInstance.result;
}
